/**
 * UserBadgeSubscriber — pushes "locked out" count via REST baseline +
 * (future) SignalR deltas from the Identity hub.
 *
 * Phase 4 of the badge migration. There is no domain event today that
 * fires on account-lock / unlock, so the counter is maintained
 * entirely by the REST baseline (on mount + on reconnect of the WS).
 * The WS hookup is kept so that when a future <c>UserLockedEvent</c>
 * lands, this subscriber can pick up deltas without further changes.
 */

import { useCallback, useEffect, useRef } from 'react';
import {
  useAdminService,
  useDomainDashboard,
  type BadgeDeltaMessage,
  type BadgeSnapshotMessage,
} from '@actschurch/ccms-sdk';
import { useEnrichedAuth } from '@/hooks/useEnrichedAuth';
import { useUserBadgesStore } from './userBadgesStore';

const ADMIN_ROLES = ['Admin'];
const DEBUG = import.meta.env.DEV;
const log = (...args: any[]) => {
  if (DEBUG) console.log('[UserBadgeSubscriber]', ...args); // eslint-disable-line no-console
};

interface UserDeltas {
  lockedOut?: number;
}

export function UserBadgeSubscriber() {
  const { hasAnyRole } = useEnrichedAuth();
  const enabled = hasAnyRole(ADMIN_ROLES);

  const adminService = useAdminService();
  const setFromMetrics = useUserBadgesStore((s) => s.setFromMetrics);
  const applyDelta = useUserBadgesStore((s) => s.applyDelta);
  const reset = useUserBadgesStore((s) => s.reset);

  const refetchBaseline = useCallback(async () => {
    try {
      const lockedOutQ = (await adminService.getUsers({
        isLockedOut: true,
        page: 1,
        pageSize: 1000,
      } as any)) as any;
      const lockedOut = Array.isArray(lockedOutQ) ? lockedOutQ.length : 0;
      log('baseline', { lockedOut });
      setFromMetrics({ lockedOut });
    } catch (err) {
      log('baseline fetch failed', err);
    }
  }, [adminService, setFromMetrics]);

  useEffect(() => {
    if (!enabled) return;
    refetchBaseline();
  }, [enabled, refetchBaseline]);

  const { isConnected, requestBadgeSnapshot } = useDomainDashboard('identity', {
    autoConnect: enabled,
    autoSubscribe: false,
    onBadgeSnapshot: (msg: BadgeSnapshotMessage<UserDeltas>) => {
      if (msg.domain !== 'Identity') return;
      log('snapshot', msg.snapshot);
      setFromMetrics(msg.snapshot ?? {});
    },
    onBadgeDelta: (msg: BadgeDeltaMessage<UserDeltas>) => {
      if (msg.domain !== 'identity') return;
      log('delta', msg.reason, msg.deltas);
      applyDelta(msg.deltas ?? {});
    },
  });

  const wasConnectedRef = useRef(false);
  useEffect(() => {
    if (!enabled) return;
    if (isConnected && !wasConnectedRef.current) {
      wasConnectedRef.current = true;
      return;
    }
    if (isConnected && wasConnectedRef.current) {
      log('reconnected → refetch baseline');
      refetchBaseline();
      void requestBadgeSnapshot();
    }
  }, [enabled, isConnected, refetchBaseline, requestBadgeSnapshot]);

  useEffect(() => () => reset(), [reset]);

  useEffect(() => {
    log('mounted', { enabled });
  }, [enabled]);

  return null;
}
